import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { HeartHandshake, BriefcaseMedical, Building2, ShieldCheck, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, homeFor, SEARCH_KEY } from "@/lib/api";

export const ROLES = [
  { key: "consumer", title: "Individual / Family", body: "I'm looking for care", icon: HeartHandshake },
  { key: "worker", title: "Healthcare Worker", body: "I'm looking for work", icon: BriefcaseMedical },
  { key: "provider", title: "Healthcare Provider", body: "I run a care organization", icon: Building2 },
];

export function destinationAfterAuth(user, search, next) {
  if (search && user.roles?.includes("consumer")) return `/find-care/results/${search}`;
  if (next && next.startsWith("/") && !next.startsWith("//")) return next;
  if (!user.onboarding_complete) return { worker: "/worker/profile", provider: "/provider/profile", consumer: "/account" }[user.primary_role] || homeFor(user);
  return homeFor(user);
}

export function startGoogle(role, search) {
  localStorage.setItem("cz_google_role", role || "consumer");
  if (search) localStorage.setItem(SEARCH_KEY, search);
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/welcome";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export const AuthShell = ({ children, side }) => (
  <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAF8F2]">
    <div className="flex flex-col p-6 sm:p-10">
      <Logo />
      <div className="flex-1 flex items-center justify-center py-10"><div className="w-full max-w-md">{children}</div></div>
    </div>
    <div className="hidden lg:block relative overflow-hidden">
      <img src="/images/zunvel-hero.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#00281F]/85 via-[#003B2D]/30 to-transparent" />
      <div className="absolute bottom-12 left-12 right-12 text-white">
        <div className="text-xs uppercase tracking-[0.25em] text-teal-300 font-semibold">Zunvel™</div>
        <div className="font-display text-3xl font-bold mt-3 leading-tight">{side || "Find Care. Find Work. Grow Your Care Business."}</div>
      </div>
    </div>
  </div>
);

export const GoogleButton = ({ onClick }) => (
  <Button type="button" variant="outline" className="w-full h-11 rounded-full bg-white" onClick={onClick} data-testid="google-signin-btn">
    <svg className="h-4 w-4 mr-2" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
    Continue with Google
  </Button>
);

export default function Login() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const search = sp.get("search") || localStorage.getItem(SEARCH_KEY);

  const finish = async (user) => {
    if (search && user.roles?.includes("consumer")) await api.post("/auth/claim-search", { search_session_id: search }).catch(() => {});
    setUser(user);
    nav(destinationAfterAuth(user, user.roles?.includes("consumer") ? search : null, sp.get("next")));
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", form);
      if (data.mfa_required) setMfa(data); else await finish(data.user);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfa.mfa_token, code }); await finish(data.user); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  if (mfa) return (
    <AuthShell side="Administrator access is protected with multi-factor authentication.">
      <div data-testid="mfa-step">
        <span className="h-12 w-12 rounded-2xl bg-teal-50 text-teal-700 grid place-items-center"><ShieldCheck className="h-6 w-6" /></span>
        <h1 className="text-2xl font-bold mt-4">{mfa.enrolled ? "Enter your verification code" : "Set up multi-factor authentication"}</h1>
        {!mfa.enrolled && (
          <div className="mt-4 space-y-3 text-sm text-slate-600">
            <p>Scan this QR code with an authenticator app (Google Authenticator, 1Password, Authy), then enter the 6-digit code.</p>
            <img src={mfa.qr_code} alt="MFA QR code" className="h-44 w-44 rounded-xl border bg-white" data-testid="mfa-qr-code" />
            <p className="font-mono-cz text-xs break-all" data-testid="mfa-secret">Manual key: {mfa.secret}</p>
          </div>
        )}
        <div className="mt-6">
          <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="mfa-code-input">
            <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}</InputOTPGroup>
          </InputOTP>
        </div>
        <Button className="w-full h-11 rounded-full bg-[#003B2D] mt-6" disabled={code.length !== 6 || busy} onClick={verify} data-testid="mfa-verify-btn">Verify and continue</Button>
      </div>
    </AuthShell>
  );

  return (
    <AuthShell>
      <h1 className="text-3xl font-bold tracking-tight text-[#003B2D]">Welcome back</h1>
      <p className="text-slate-500 mt-1.5">{search ? "Sign in to unlock your saved care matches." : "Sign in to your Zunvel account."}</p>
      <form onSubmit={submit} className="space-y-4 mt-8" data-testid="login-form">
        <div className="space-y-1.5"><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" data-testid="login-email-input" /></div>
        <div className="space-y-1.5"><div className="flex justify-between"><Label>Password</Label><Link to="/forgot-password" className="text-sm text-teal-700 font-medium" data-testid="forgot-password-link">Forgot password?</Link></div>
          <Input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11" data-testid="login-password-input" /></div>
        <Button type="submit" className="w-full h-11 rounded-full bg-[#003B2D] hover:bg-[#00281F]" disabled={busy} data-testid="login-submit-btn"><KeyRound className="h-4 w-4 mr-1.5" />{busy ? "Signing in…" : "Sign in"}</Button>
      </form>
      <div className="my-6 flex items-center gap-3 text-xs text-slate-400"><div className="h-px bg-slate-200 flex-1" />OR<div className="h-px bg-slate-200 flex-1" /></div>
      <GoogleButton onClick={() => startGoogle("consumer", search)} />
      <p className="text-xs text-slate-400 mt-3 text-center">Apple sign-in is planned for the mobile app release.</p>
      <p className="text-sm text-slate-600 mt-8 text-center">New to Zunvel? <Link to={`/register${search ? `?search=${search}&role=consumer` : ""}`} className="text-teal-700 font-semibold" data-testid="go-register-link">Create a free account</Link></p>
    </AuthShell>
  );
}
